import type { CSSProperties, ReactNode } from "react";
import {
  assembleEvidenceCenter,
  evidenceSourceLabel,
  type AssembleEvidenceCenterInput,
  type EvidenceCenterState,
  type EvidenceTimelineEntry,
} from "../evidenceCenter";
import { LABEL } from "../terminology";

export type EvidenceCenterPanelProps = {
  /** Pre-assembled state, or pass `input` to assemble in the panel. */
  state?: EvidenceCenterState | null;
  input?: AssembleEvidenceCenterInput | null;
  /** Called when the operator requests container log fetch. */
  onFetchLogs?: () => void;
  /** True while GET .../logs is in flight. */
  logsLoading?: boolean;
  /** Focus the logs section on open (View Logs entry point). */
  focusLogs?: boolean;
  className?: string;
  style?: CSSProperties;
};

/**
 * Shared Verification Center chrome for Ops + Control Plane.
 * Displays one incident/tenant verification audit trail; logs are one source among many.
 */
export function EvidenceCenterPanel({
  state: stateProp,
  input,
  onFetchLogs,
  logsLoading = false,
  focusLogs = false,
  className = "",
  style,
}: EvidenceCenterPanelProps): ReactNode {
  const state =
    stateProp ??
    (input
      ? assembleEvidenceCenter(input)
      : null);

  if (!state) {
    return (
      <div className={`evidence-center-panel ${className}`.trim()} style={style}>
        <p style={{ margin: 0, fontSize: "0.85rem", color: "#94a3b8" }}>
          No evidence assembled.
        </p>
      </div>
    );
  }

  const logs = state.logs;
  const showLogs =
    focusLogs ||
    logs.status === "available" ||
    logs.status === "unavailable" ||
    logs.status === "loading" ||
    logsLoading;

  return (
    <div
      className={`evidence-center-panel ${className}`.trim()}
      style={style}
      data-slug={state.slug}
      data-incident={state.incidentId ?? undefined}
    >
      <header style={{ marginBottom: "0.85rem" }}>
        <p
          style={{
            margin: 0,
            fontSize: "0.7rem",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "#94a3b8",
          }}
        >
          Verification Center
        </p>
        <h3
          style={{
            margin: "0.25rem 0 0",
            fontSize: "1rem",
            fontWeight: 600,
            color: "#f1f5f9",
          }}
        >
          {state.title}
        </h3>
        <p style={{ margin: "0.35rem 0 0", fontSize: "0.8rem", color: "#cbd5e1" }}>
          {state.summary}
        </p>
        {state.rootCause ? (
          <p style={{ margin: "0.35rem 0 0", fontSize: "0.8rem", color: "#cbd5e1" }}>
            <span style={{ color: "#94a3b8" }}>Root cause: </span>
            {state.rootCause}
          </p>
        ) : null}
      </header>

      <section style={{ marginBottom: "1rem" }}>
        <h4
          style={{
            margin: "0 0 0.5rem",
            fontSize: "0.7rem",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "#94a3b8",
          }}
        >
          Evidence timeline
        </h4>
        {state.timeline.length === 0 ? (
          <p style={{ margin: 0, fontSize: "0.8rem", color: "#64748b" }}>
            No evidence entries yet.
          </p>
        ) : (
          <ol
            style={{
              margin: 0,
              padding: 0,
              listStyle: "none",
              display: "grid",
              gap: "0.5rem",
            }}
          >
            {state.timeline.map((entry) => (
              <TimelineRow key={entry.id} entry={entry} />
            ))}
          </ol>
        )}
      </section>

      <section
        id={`evidence-logs-${state.slug}`}
        style={{
          padding: "0.75rem",
          borderRadius: "0.5rem",
          border: "1px solid #1e293b",
          background: "rgba(2, 6, 23, 0.35)",
        }}
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "0.5rem",
          }}
        >
          <h4
            style={{
              margin: 0,
              fontSize: "0.7rem",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "#94a3b8",
            }}
          >
            Container logs
          </h4>
          {onFetchLogs ? (
            <button
              type="button"
              onClick={onFetchLogs}
              disabled={logsLoading}
              style={{
                fontSize: "0.75rem",
                padding: "0.35rem 0.65rem",
                borderRadius: "0.375rem",
                border: "1px solid #334155",
                background: "#0f172a",
                color: "#e2e8f0",
                cursor: logsLoading ? "wait" : "pointer",
              }}
            >
              {logsLoading ? "Fetching…" : "Fetch logs"}
            </button>
          ) : null}
        </div>

        {logs.error ? (
          <p style={{ margin: "0.5rem 0 0", fontSize: "0.8rem", color: "#fb7185" }} role="alert">
            {logs.error}
          </p>
        ) : null}
        {logs.message && !logs.error ? (
          <p style={{ margin: "0.5rem 0 0", fontSize: "0.75rem", color: "#94a3b8" }}>
            {logs.message}
          </p>
        ) : null}

        {showLogs && logs.lines.length > 0 ? (
          <pre
            style={{
              margin: "0.65rem 0 0",
              maxHeight: "18rem",
              overflow: "auto",
              padding: "0.65rem",
              borderRadius: "0.375rem",
              background: "#020617",
              border: "1px solid #1e293b",
              fontSize: "0.7rem",
              lineHeight: 1.45,
              color: "#cbd5e1",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {logs.lines.join("\n")}
          </pre>
        ) : showLogs && !logsLoading ? (
          <p style={{ margin: "0.5rem 0 0", fontSize: "0.75rem", color: "#64748b" }}>
            {logs.status === "unavailable"
              ? "No container log output returned."
              : "Logs not loaded yet."}
          </p>
        ) : null}

        {logs.command ? (
          <p
            style={{
              margin: "0.65rem 0 0",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontSize: "0.7rem",
              color: "#64748b",
            }}
            title="Host CLI fallback"
          >
            CLI: {logs.command}
          </p>
        ) : null}
        {logs.fetchedAt ? (
          <p style={{ margin: "0.35rem 0 0", fontSize: "0.7rem", color: "#64748b" }}>
            Fetched: {logs.fetchedAt}
          </p>
        ) : null}
      </section>

      {state.recommendedActions.length > 0 ? (
        <section style={{ marginTop: "0.85rem" }}>
          <h4
            style={{
              margin: "0 0 0.35rem",
              fontSize: "0.7rem",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "#94a3b8",
            }}
          >
            Recommended actions
          </h4>
          <ul style={{ margin: 0, paddingLeft: "1.1rem", fontSize: "0.8rem", color: "#cbd5e1" }}>
            {state.recommendedActions.map((action) => (
              <li key={action}>{action}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {state.aiAnalysis ? (
        <section style={{ marginTop: "0.85rem" }}>
          <h4
            style={{
              margin: "0 0 0.35rem",
              fontSize: "0.7rem",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "#94a3b8",
            }}
          >
            AI analysis
          </h4>
          <p style={{ margin: 0, fontSize: "0.8rem", color: "#cbd5e1" }}>{state.aiAnalysis}</p>
        </section>
      ) : null}
    </div>
  );
}

function TimelineRow({ entry }: { entry: EvidenceTimelineEntry }): ReactNode {
  return (
    <li
      style={{
        padding: "0.55rem 0.65rem",
        borderRadius: "0.375rem",
        border: "1px solid #1e293b",
        background: entry.stub ? "rgba(15, 23, 42, 0.4)" : "rgba(2, 6, 23, 0.45)",
      }}
    >
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "0.35rem 0.65rem",
          alignItems: "baseline",
          justifyContent: "space-between",
        }}
      >
        <strong style={{ fontSize: "0.8rem", color: "#e2e8f0" }}>{entry.title}</strong>
        <span style={{ fontSize: "0.65rem", color: "#64748b" }}>
          {evidenceSourceLabel(entry.source)}
          {entry.stub ? ` · ${LABEL.notAvailable}` : ""}
          {entry.severity ? ` · ${entry.severity}` : ""}
        </span>
      </div>
      <p style={{ margin: "0.25rem 0 0", fontSize: "0.75rem", color: "#94a3b8" }}>
        {entry.detail}
      </p>
      {entry.observedAt ? (
        <p style={{ margin: "0.2rem 0 0", fontSize: "0.65rem", color: "#64748b" }}>
          {entry.observedAt}
        </p>
      ) : null}
      {entry.href ? (
        <a
          href={entry.href}
          style={{ display: "inline-block", marginTop: "0.25rem", fontSize: "0.7rem", color: "#7dd3fc" }}
        >
          Open linked evidence
        </a>
      ) : null}
      {entry.logLines && entry.logLines.length > 0 ? (
        <pre
          style={{
            margin: "0.4rem 0 0",
            maxHeight: "8rem",
            overflow: "auto",
            padding: "0.4rem",
            fontSize: "0.65rem",
            color: "#cbd5e1",
            background: "#020617",
            borderRadius: "0.25rem",
          }}
        >
          {entry.logLines.slice(0, 40).join("\n")}
        </pre>
      ) : null}
    </li>
  );
}

/** Alias — Verification Center is the product name for this panel. */
export type VerificationCenterPanelProps = EvidenceCenterPanelProps;
export const VerificationCenterPanel = EvidenceCenterPanel;
